import { Injectable, inject } from "@angular/core";
import { DataContainer } from "app/shared/model";
import { ExternalDataServiceProxy } from "app/shared/services/external-data-service.proxy";
import { GetControlOptionsResponse, GetExternalDataResponse, SearchCloudDataResponse } from "app/shared/services/model/external-data-service.model";
import { Observable, Subject, catchError, debounceTime, shareReplay, switchMap, take, tap, throwError } from "rxjs";

@Injectable({
    providedIn: 'root'
})
export class ExternalDataService {
    private _externalDataServiceProxy = inject(ExternalDataServiceProxy);
    private requestStreams = new Map<string, Subject<any>>();
    private responseStreams = new Map<string, Observable<GetExternalDataResponse>>();

    constructor() {
    }

    private navigateOnError(error: string | undefined) {
        console.error(error);
        //  this._router.navigateByUrl('/error/400');
        return throwError(() => new Error(error));
    };

    getPaymentDefaults(formId: string, applicationId: string, paymentProviderKey: string): Observable<string> {
        return this._externalDataServiceProxy.getPaymentDefaults(formId, applicationId, paymentProviderKey).pipe(
            tap((result: string) => {
                // console.log('ExternalDataService.getPaymentDefaults():', result);
                return result;
            }),
            catchError(this.navigateOnError)
        );
    }

    getExternalData(formId: string, formVersion: number | undefined, pageId: number, applicationId: string, applicationData: string): Observable<GetExternalDataResponse> {
        // return this._externalDataServiceProxy.getExternalData(formId, formVersion, pageId, applicationId, applicationData).pipe(
        //     tap((result: GetExternalDataResponse) => {
        //         // console.log('ExternalDataService.getExternalData():', result);
        //         return result;
        //     }),
        //     catchError(this.navigateOnError)
        // );

        const key = JSON.stringify({ formId, formVersion, pageId, applicationId, applicationData });

        // Create stream if it doesn't exist
        if (!this.requestStreams.has(key)) {
            const trigger$ = new Subject<void>();
            const response$ = trigger$.pipe(debounceTime(200), // debounce requests
                switchMap(() =>
                    this._externalDataServiceProxy
                        .getExternalData(formId, formVersion, pageId, applicationId, applicationData)
                        .pipe(
                            tap(() => {
                                this.requestStreams.delete(key);
                                this.responseStreams.delete(key);
                            }),
                            catchError((err) => {
                                this.requestStreams.delete(key);
                                this.responseStreams.delete(key);
                                return this.navigateOnError(err);
                            })
                        )
                ),
                shareReplay({ bufferSize: 1, refCount: true }) // share + cache latest value
            );

            this.requestStreams.set(key, trigger$);
            this.responseStreams.set(key, response$);
        }

        return new Observable<GetExternalDataResponse>(subscriber => {
            const responseSubscription = this.responseStreams.get(key)!
                .pipe(take(1))
                .subscribe(subscriber);

            this.requestStreams.get(key)!.next({});

            return () => responseSubscription.unsubscribe();
        });

        // Return shared observable
        return this.responseStreams.get(key)!;
    }

    searchExternalData(formId: string, formVersion: number | undefined, controlName: string, searchTerm: string, applicationId: string, applicationData: string): Observable<GetExternalDataResponse> {
        return this._externalDataServiceProxy.searchExternalData(formId, formVersion, controlName, searchTerm, applicationId, applicationData).pipe(
            tap((result) => {
                // console.log('ExternalDataService.searchExternalData():', result);
                return result;
            }),
            catchError(this.navigateOnError)
        );
    }

    searchCloudData(formId: string, formVersion: number | undefined, controlName: string, searchTerm: string, category: string, pageId: number, applicationId: string, searchByValue: boolean): Observable<SearchCloudDataResponse> {
        return this._externalDataServiceProxy.searchCloudData(formId, formVersion, controlName, searchTerm, category, pageId, applicationId, searchByValue).pipe(
            tap((result: SearchCloudDataResponse) => {
                // console.log('ExternalDataService.searchCloudData():', result);
                return result;
            }),
            catchError(this.navigateOnError)
        );
    }

    getControlOptions(formId: string, formVersion: number | undefined, controlName: string, applicationData: DataContainer, applicationId: string): Observable<GetControlOptionsResponse> {
        return this._externalDataServiceProxy.getControlOptions(formId, formVersion, controlName, applicationData, applicationId).pipe(
            tap((result: GetControlOptionsResponse) => {
                // console.log('ExternalDataService.getControlOptions():', result);
                return result;
            }),
            catchError(this.navigateOnError)
        );
    }
}
